import React from 'react';
import type { AdminAccount, AdminContentItem, AdminSessionItem, HeartSettings } from '../../lib/types';

interface OverviewSectionProps {
  settings: HeartSettings | null;
  accounts: AdminAccount[];
  lovePhrases: AdminContentItem[];
  nicknames: AdminContentItem[];
  sessions: AdminSessionItem[];
  onOpenPreview: () => void;
  onNavigateTab: (tab: 'content' | 'accounts' | 'settings' | 'sessions') => void;
}

export const OverviewSection: React.FC<OverviewSectionProps> = ({
  settings,
  accounts,
  lovePhrases,
  nicknames,
  sessions,
  onOpenPreview,
  onNavigateTab,
}) => {
  const activePhrases = lovePhrases.filter(p => p.isActive).length;
  const activeNicknames = nicknames.filter(n => n.isActive).length;

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return 'Never';
    try {
      return new Date(dateStr).toLocaleString(undefined, {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return dateStr;
    }
  };

  return (
    <div className="admin-section overview-section">
      <div className="section-header">
        <h2>Overview</h2>
        <p className="section-description">
          Current state and active configuration of the keepsake.
        </p>
      </div>

      <div className="stats-grid">
        <div className="stat-card" onClick={() => onNavigateTab('content')}>
          <span className="stat-label">Active Love Phrases</span>
          <span className="stat-value">{activePhrases}</span>
          <span className="stat-sub">{lovePhrases.length} total registered</span>
        </div>

        <div className="stat-card" onClick={() => onNavigateTab('content')}>
          <span className="stat-label">Active Nicknames</span>
          <span className="stat-value">{activeNicknames}</span>
          <span className="stat-sub">{nicknames.length} total registered</span>
        </div>

        <div className="stat-card" onClick={() => onNavigateTab('settings')}>
          <span className="stat-label">Phrase Distribution</span>
          <span className="stat-value">
            {settings?.love_ratio ?? 70}% / {settings?.nickname_ratio ?? 30}%
          </span>
          <span className="stat-sub">Love phrases vs nicknames</span>
        </div>

        <div className="stat-card" onClick={() => onNavigateTab('sessions')}>
          <span className="stat-label">Active Sessions</span>
          <span className="stat-value">{sessions.length}</span>
          <span className="stat-sub">Duration: {settings?.session_duration_hours ?? 24} hours</span>
        </div>

        <div className="stat-card" onClick={() => onNavigateTab('accounts')}>
          <span className="stat-label">Registered Accounts</span>
          <span className="stat-value">{accounts.length}</span>
          <span className="stat-sub">1 Admin, 1 Viewer</span>
        </div>

        <div className="stat-card" onClick={() => onNavigateTab('settings')}>
          <span className="stat-label">Last Updated</span>
          <span className="stat-value text-small">{formatDate(settings?.updated_at)}</span>
          <span className="stat-sub">Settings timestamp</span>
        </div>
      </div>

      <div className="overview-cta-box">
        <div className="cta-info">
          <h3>Viewer Experience Preview</h3>
          <p>Inspect the typographic heart composition as the viewer sees it.</p>
        </div>
        <button type="button" className="action-btn action-btn-preview" onClick={onOpenPreview}>
          Launch Live Preview
        </button>
      </div>
    </div>
  );
};
